"use client";

import { LOCATION_COPY } from "@1-now/booking-core";
import { Dialog, DialogPanel } from "@headlessui/react";
import { MagnifyingGlassIcon, MapPinIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FC, ReactNode, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { useData } from "data/data-provider";
import RentalCarDatesRangeInput from "designs/Common/(car-search-form)/RentalCarDatesRangeInput";
import RentalCarTimesRangeInput from "designs/Common/(car-search-form)/RentalCarTimesRangeInput";
import EndDateTimeRange from "designs/Common/(car-search-form)/endDateTime";
import StartDateTimeRange from "designs/Common/(car-search-form)/startDateTime";
import DropOffLocationInput from "designs/Common/DropOffLocationInput";
import LocationInput from "designs/Common/LocationInput";
import { useReturnSwitch } from "designs/Common/useReturnSwitch";

/**
 * 1NOW-11153: the search bar in the top bar, same as the DFW site.
 *
 * Desktop (xl, 1280 and up): the compact form in the header. Below xl: a two-line
 * trip pill (option C) that opens the same form full screen. It is the home page form's own
 * fields and the home page's own call (callAvailableFleetAPI, then
 * /available-car), so the dates, times and locations are shared with the hero
 * form through the data provider. Colours are the tenant's theme tokens only.
 *
 * The caller decides WHEN it shows (useTopBarSearchVisible) and renders it in
 * the middle of the header row; `logo` is the header's own brand mark, shown in
 * the full-screen form.
 */

// The full form needs ~1280px beside the logo and the menu button; at 1024-1279 it was cut off,
// and the time box, search button and menu fell off the screen. Below 1280 the "Search" pill opens it.
const DESKTOP_QUERY = "(min-width: 1280px)";

const useIsDesktop = () => {
  const read = () =>
    typeof window.matchMedia === "function" &&
    window.matchMedia(DESKTOP_QUERY).matches;
  const [isDesktop, setIsDesktop] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
};

/** The home page search, as Design B's search button runs it. `then` runs once
 *  the results are in and the page is about to move. */
const useSubmit = (then?: () => void) => {
  const navigate = useNavigate();
  const {
    pickupLocation,
    dropOffLocation,
    dropoffLocationType,
    startDate,
    endDate,
    callAvailableFleetAPI,
    loadingAvailableFleet,
  } = useData();
  const submit = () => {
    if (pickupLocation === null) {
      toast.error("Please enter Pick up location");
    } else if (dropOffLocation === null && dropoffLocationType === false) {
      toast.error("Please enter Drop off location");
    } else if (startDate === null || endDate === null) {
      toast.error("Please enter start and end date");
    } else {
      callAvailableFleetAPI((path: string) => {
        then?.();
        navigate(path);
      });
    }
  };
  return { submit, loading: loadingAvailableFleet };
};

const Spinner = () => (
  <span
    aria-hidden="true"
    className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"
  />
);

/** The pick-up / return location fields and the dates, laid out by `stacked`
 *  (the full-screen form) or in one row (the header). */
const Fields: FC<{ stacked?: boolean }> = ({ stacked }) => {
  const { ourCompany, dropoffLocationType } = useData();
  const { isDifferent, canSwitch, toggle } = useReturnSwitch();
  // Same rule as the home page: the tabbed picker when long-term or hourly
  // rentals are on, the plain date range and time range otherwise.
  const rich = Boolean(
    ourCompany?.allow_long_term_rentals || ourCompany?.allow_hourly_rentals,
  );
  const box = stacked
    ? "rounded-lg border border-text-color/15 bg-secondary-50"
    : "";
  const pickUp = isDifferent
    ? LOCATION_COPY.pickUp
    : LOCATION_COPY.same;

  return (
    <>
      <div
        className={`flex flex-col ${
          stacked ? "min-w-0" : "min-w-[8rem] flex-[1.3]"
        }`}
      >
        <LocationInput
          placeHolder={pickUp.label}
          desc={pickUp.prompt}
          className={`w-full ${box}`}
          sameDropOffLocation={isDifferent}
        />
        {canSwitch && (
          <button
            type="button"
            onClick={toggle}
            className={`truncate text-left text-xs text-secondary-text-color hover:text-text-color ${
              stacked ? "mt-2" : "-mt-1 px-3 pb-1"
            }`}
          >
            {isDifferent ? "- Same return location" : "+ Different return location"}
          </button>
        )}
      </div>
      {isDifferent && (
        <DropOffLocationInput
          placeHolder={LOCATION_COPY.dropOff.label}
          desc={LOCATION_COPY.dropOff.prompt}
          className={`${stacked ? "" : "flex-1"} ${box}`}
        />
      )}
      {rich ? (
        <>
          <StartDateTimeRange className={`${stacked ? "" : "flex-1"} ${box}`} />
          <EndDateTimeRange
            className={`${stacked ? "" : "flex-1"} ${box}`}
            dropOffLocationType={dropoffLocationType}
            hasButtonSubmit={false}
          />
        </>
      ) : (
        <>
          <RentalCarDatesRangeInput
            className={`${stacked ? "" : "flex-1"} ${box}`}
          />
          <RentalCarTimesRangeInput
            dropOffLocationType={dropoffLocationType}
            className={`${stacked ? "" : "flex-1"} ${box}`}
            hasButtonSubmit={false}
          />
        </>
      )}
    </>
  );
};

const DesktopForm: FC = () => {
  const { submit, loading } = useSubmit();
  return (
    <div className="flex min-w-0 flex-1 justify-center px-3 xl:px-6">
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="nc-topbar-search flex w-full max-w-4xl items-center gap-1.5 rounded-xl border border-text-color/15 bg-secondary-50 p-1"
      >
        <div className="flex min-w-0 flex-1 items-stretch divide-x divide-text-color/10">
          <Fields />
        </div>
        <button
          type="submit"
          disabled={loading}
          aria-label="Search available cars"
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-primary-brand text-on-primary transition-colors hover:bg-primary-700 disabled:opacity-70"
        >
          {loading ? <Spinner /> : <MagnifyingGlassIcon className="h-5 w-5" />}
        </button>
      </form>
    </div>
  );
};

const SearchModal: FC<{ onClose: () => void; logo?: ReactNode }> = ({
  onClose,
  logo,
}) => {
  const { submit, loading } = useSubmit(onClose);
  // Over the pinned bar it opens from (z-[60] on Design B's home), which used to cover the Close button.
  return (
    <Dialog open onClose={onClose} className="relative z-[70]">
      <DialogPanel className="fixed inset-0 overflow-y-auto bg-bg-color text-text-color">
        <div className="flex items-center justify-between p-4">
          {logo}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            className="ml-auto rounded-full border border-text-color/30 p-2 transition-colors hover:bg-secondary-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex flex-col gap-4 px-6 py-4"
        >
          <Fields stacked />
          <button
            type="submit"
            disabled={loading}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-primary-brand py-4 font-semibold text-on-primary transition-colors hover:bg-primary-700 disabled:opacity-70"
          >
            {loading ? (
              <Spinner />
            ) : (
              <>
                <MagnifyingGlassIcon className="h-5 w-5" />
                <span>Search cars</span>
              </>
            )}
          </button>
        </form>
      </DialogPanel>
    </Dialog>
  );
};

/** "Oct 10–13", "Oct 30 – Nov 2" or "Oct 10": the trip's dates as the pill's second line says them. */
const tripDates = (start: unknown, end: unknown): string | null => {
  const a = start ? new Date(start as Date) : null;
  const b = end ? new Date(end as Date) : null;
  if (!a || !b || isNaN(a.getTime()) || isNaN(b.getTime())) return null;
  const md = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (a.toDateString() === b.toDateString()) return md(a);
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) return `${md(a)}–${b.getDate()}`;
  return `${md(a)} – ${md(b)}`;
};

/**
 * The phone pill, option C (Taha, 7 Oct). With no trip yet it reads "Search cars · where & when";
 * once a location or dates are chosen it shows the trip itself ("Orlando Airport · Oct 10–13") with
 * a pencil, and what is not chosen yet as "Pick-up location" / "Add dates". One tap target that opens
 * the search sheet, pre-filled from the same trip state.
 *
 * The dates never give way: when the trip is longer than the pill, the location is cut with an
 * ellipsis and the dates stay whole (1NOW-11174 reopen, 1NOW-11192).
 *
 * `compact` is the bar that also carries the brand mark left of the pill (Design B on phones): the
 * pill has less room, so with no trip it asks "Where and when?" instead of the longer line, and the
 * bar's own gaps give it its edges. Under 360px the pin goes too, so the dates stay whole at 320.
 */
const PhonePill: FC<{ onOpen: () => void; compact?: boolean }> = ({ onOpen, compact = false }) => {
  const { pickupLocation, startDate, endDate } = useData();
  const where: string | null = pickupLocation?.name || null;
  const when = tripDates(startDate, endDate);
  const hasTrip = Boolean(where || when);
  const place = where ?? "Pick-up location";
  const dates = when ?? "Add dates";
  return (
    <div className={`flex min-w-0 flex-1 justify-center ${compact ? "" : "px-2"}`}>
      <button
        type="button"
        onClick={onOpen}
        aria-haspopup="dialog"
        aria-label={hasTrip ? `Edit trip: ${place} · ${dates}` : "Search cars: where and when"}
        className="flex h-11 w-full max-w-xs items-center gap-2 rounded-full border border-text-color/15 bg-secondary-50 pl-3.5 pr-3 text-left text-text-color transition-opacity hover:opacity-90 max-[359px]:pl-3 max-[359px]:pr-2.5"
      >
        {hasTrip ? (
          <MapPinIcon className="h-5 w-5 flex-shrink-0 max-[359px]:hidden" aria-hidden="true" />
        ) : (
          <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        )}
        <span className="flex min-w-0 flex-1 items-baseline gap-1 whitespace-nowrap text-sm">
          {hasTrip ? (
            <>
              <span className="min-w-0 truncate font-semibold">{place}</span>
              <span className="flex-shrink-0 text-secondary-text-color">· {dates}</span>
            </>
          ) : compact ? (
            <span className="min-w-0 truncate font-semibold">Where and when?</span>
          ) : (
            <>
              <span className="flex-shrink-0 font-semibold">Search cars</span>
              <span className="min-w-0 truncate text-secondary-text-color">· where &amp; when</span>
            </>
          )}
        </span>
        {hasTrip && <PencilIcon className="h-4 w-4 flex-shrink-0 text-secondary-text-color" aria-hidden="true" />}
      </button>
    </div>
  );
};

const TopBarSearch: FC<{ logo?: ReactNode; compact?: boolean }> = ({ logo, compact = false }) => {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);

  // Leaving the phone width with the full-screen form open would strand it.
  useEffect(() => {
    if (isDesktop) setOpen(false);
  }, [isDesktop]);

  if (isDesktop) return <DesktopForm />;
  return (
    <>
      <PhonePill onOpen={() => setOpen(true)} compact={compact} />
      {open && <SearchModal onClose={() => setOpen(false)} logo={logo} />}
    </>
  );
};

export default TopBarSearch;
